/**
 * Query Editor MCP Command Store
 *
 * Holds pending MCP commands for query editor tabs.
 * The QueryEditorTab component subscribes to this store and executes commands.
 */

import { create } from 'zustand';
import type { ChartConfig } from '../../charts/types/chartTypes';
import type { QueryResultTab, QueryResultDisplayMode } from '../../../../types/mcpUiControl';

export type QueryEditorMcpCommand =
  | { type: 'select_row'; rowIndex: number; resultSetIndex?: number }
  | { type: 'show_row_details'; show: boolean; rowIndex?: number }
  | { type: 'switch_tab'; resultTab: QueryResultTab; resultSetIndex?: number }
  | { type: 'set_result_display'; displayMode: QueryResultDisplayMode }
  /**
   * Split by mode rather than carrying an optional binding: `actual` runs the
   * statement and therefore cannot be queued without what the permission gate
   * classified, while `estimated` only compiles it and has nothing to bind.
   * A single variant with an optional field would make "actual, unbound"
   * representable — the state this shape exists to remove.
   */
  | { type: 'execute_with_plan'; planMode: 'actual'; allowedSources: string[] }
  | { type: 'execute_with_plan'; planMode: 'estimated' }
  | { type: 'set_chart_config'; config: ChartConfig; resultSetIndex?: number }
  | { type: 'show_chart'; show: boolean };

type PendingCommand = {
  id: string;
  tabId: string;
  command: QueryEditorMcpCommand;
};

type QueryEditorMcpState = {
  /** Pending commands for query editor tabs */
  pendingCommands: PendingCommand[];
  /** Queue a command for a specific tab */
  queueCommand: (tabId: string, command: QueryEditorMcpCommand) => string;
  /** Get pending commands for a specific tab */
  getPendingCommands: (tabId: string) => PendingCommand[];
  /** Mark a command as completed */
  completeCommand: (commandId: string) => void;
  /** Clear all pending commands for a tab */
  clearTabCommands: (tabId: string) => void;
};

let commandIdCounter = 0;

export const useQueryEditorMcpStore = create<QueryEditorMcpState>((set, get) => ({
  pendingCommands: [],

  queueCommand: (tabId: string, command: QueryEditorMcpCommand): string => {
    const id = `mcp-query-cmd-${++commandIdCounter}`;
    console.log(`[QueryEditorMcpStore] Queuing command ${id} for tab ${tabId}:`, command.type, command);
    set((state) => ({
      pendingCommands: [...state.pendingCommands, { id, tabId, command }],
    }));
    console.log(`[QueryEditorMcpStore] After queue, pendingCommands count: ${get().pendingCommands.length}`);
    return id;
  },

  getPendingCommands: (tabId: string): PendingCommand[] => {
    const commands = get().pendingCommands.filter((cmd) => cmd.tabId === tabId);
    console.log(`[QueryEditorMcpStore] getPendingCommands for tab ${tabId}: ${commands.length} commands`);
    return commands;
  },

  completeCommand: (commandId: string) => {
    console.log(`[QueryEditorMcpStore] Completing command: ${commandId}`);
    set((state) => ({
      pendingCommands: state.pendingCommands.filter((cmd) => cmd.id !== commandId),
    }));
  },

  clearTabCommands: (tabId: string) => {
    console.log(`[QueryEditorMcpStore] Clearing commands for tab: ${tabId}`);
    set((state) => ({
      pendingCommands: state.pendingCommands.filter((cmd) => cmd.tabId !== tabId),
    }));
  },
}));
